/**
 * Recovery for chunk load failures after a deploy (version skew).
 *
 * A tab opened before a deploy still runs the old client bundle. When it lazily loads a code
 * chunk, it asks for that build's hashed file, which the new deployment no longer serves, so
 * the import rejects with a `ChunkLoadError`. Retrying in place asks for the same missing file;
 * only a full reload picks up the new HTML and the new chunk names.
 */

/** sessionStorage key holding the time of the last chunk-error reload in this tab. */
const CHUNK_RELOAD_STORAGE_KEY = "chunk-load-error-reload-at";

/**
 * A second chunk failure within this window of the last reload is a real outage, not skew:
 * stop reloading and let the error surface instead of looping.
 */
const CHUNK_RELOAD_WINDOW_MS = 30_000;

/** Messages Turbopack, webpack and native dynamic `import()` use when a chunk fails to load. */
const CHUNK_LOAD_ERROR_PATTERNS: RegExp[] = [
  /Failed to load chunk/i,
  /Loading (CSS )?chunk [\w-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

/**
 * Tells whether a thrown value is a failed chunk load.
 *
 * @param error - Any thrown value or promise rejection reason.
 * @returns `true` when it is a `ChunkLoadError` or carries a chunk-load failure message.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === "ChunkLoadError") return true;
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(error.message));
}

/**
 * Hard-reloads the page once so the tab fetches the current deployment's assets. Guarded in
 * sessionStorage so a chunk that is missing on the current deployment too cannot loop.
 *
 * @returns `true` when a reload was started; `false` when one already ran within
 *   {@link CHUNK_RELOAD_WINDOW_MS} or sessionStorage is unavailable (show the error instead).
 */
export function reloadOnceForChunkLoadError(): boolean {
  if (typeof window === "undefined") return false;

  const now = Date.now();
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY));
    if (Number.isFinite(lastReloadAt) && now - lastReloadAt < CHUNK_RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
  } catch {
    return false; // can't persist the guard, so a reload could loop
  }

  window.location.reload();
  return true;
}

/**
 * Listens for chunk load failures that escape React error boundaries (a lazy `import()` in an
 * event handler or effect rejects as an unhandled promise) and reloads once to recover.
 *
 * @returns Nothing. Call once, on the client, before the app hydrates.
 */
export function installChunkLoadRecovery(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) reloadOnceForChunkLoadError();
  });
  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error)) reloadOnceForChunkLoadError();
  });
}
